import React, { useEffect, useRef, useState } from 'react';
import { removeUnitCoLauncher, restoreUnitCoLauncher } from '../../utils/unitcoLauncherCleanup';

interface UnitWhiteLabelActivityProps {
  cardType?: string;
  jwtToken: string;
  currentUserId?: string | null;
  accountId?: string | null; // Account ID for the activity component
  customerToken?: string; // Customer token (alternative to jwtToken)
  themeUrl?: string;
  languageUrl?: string;
  queryFilter?: string; // Query filter string like "filter[since]=2023-01-01T00:00:00.000Z&filter[until]=2023-04-26T00:00:00.000Z&sort=-createdAt"
  hideTitle?: boolean;
  hideFilterButton?: boolean;
  hideBackToTop?: boolean;
  showDeclinedActivity?: boolean;
  showCsvDownload?: boolean;
  onLoadComplete?: () => void;
}

const UnitWhiteLabelActivity: React.FC<UnitWhiteLabelActivityProps> = ({
  cardType = "",
  jwtToken,
  currentUserId: propCurrentUserId,
  accountId,
  customerToken,
  themeUrl,
  languageUrl,
  queryFilter,
  hideTitle = false,
  hideFilterButton = false,
  hideBackToTop = false,
  showDeclinedActivity = true,
  showCsvDownload = true,
  onLoadComplete
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [componentKey, setComponentKey] = useState<string>('');
  const hasInitializedRef = useRef<boolean>(false);

  // Use prop currentUserId directly and update componentKey when it changes
  useEffect(() => {
    if (propCurrentUserId) {
      setComponentKey(`${propCurrentUserId}_${Date.now()}`);
    } else {
      setComponentKey('');
    }
  }, [propCurrentUserId]);

  // Load Unit.co SDK and create component in a single useEffect
  useEffect(() => {
    if (!propCurrentUserId || !jwtToken) {
      hasInitializedRef.current = false;
      return;
    }

    // Restore launcher visibility when (re)entering Activities
    restoreUnitCoLauncher();
    
    // If already initialized for this user and token, skip
    if (hasInitializedRef.current) {
      return;
    }
    
    try {
      // Mock Zendesk to prevent Unit.co errors
      if (!window.zE) {
        window.zE = function() {
          // Mock Zendesk function - do nothing
        };
      }
      
      const scriptId = 'unit-wl-activity-script';
      const env = process.env.REACT_APP_FIREBASE_ENV;
      let scriptUrl = 'https://ui.s.unit.sh/release/latest/components-extended.js';
      if (env === 'production') {
        scriptUrl = 'https://ui.unit.co/release/latest/components-extended.js';
      }
      
      // Remove existing script
      const existingScript = document.getElementById(scriptId) as HTMLScriptElement;
      if (existingScript) {
        existingScript.remove();
      }
      
      // Clear container
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      
      // Add cache-busting
      const cacheBuster = `_cb=${Date.now()}_${propCurrentUserId}`;
      const scriptUrlWithCacheBuster = `${scriptUrl}?${cacheBuster}`;
      
      // Load script
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = scriptUrlWithCacheBuster;
      script.async = true;
      
      // Create Unit.co element after script loads
      script.onload = () => {
        try {
          if (!containerRef.current) {
            console.error('❌ UnitWhiteLabelActivity: Container ref is null');
            return;
          }
          
          // Clear container again
          containerRef.current.innerHTML = '';
          
          // Create Unit.co activity element
          const el = document.createElement('unit-elements-activity');
          
          // Set account-id if provided
          if (accountId) {
            el.setAttribute('account-id', accountId);
          }
          
          // Set jwt-token so Unit script uses it for theme/identity (e.g. /white-label/theme/default, /identity)
          if (jwtToken) {
            el.setAttribute('jwt-token', jwtToken);
          }
          
          // Set customer-token for activity API (use provided customerToken or jwtToken as fallback)
          if (customerToken) {
            el.setAttribute('customer-token', customerToken);
          } else if (jwtToken) {
            el.setAttribute('customer-token', jwtToken);
          }
          
          // Set theme if provided
          if (themeUrl) {
            el.setAttribute('theme', themeUrl);
          }
          
          // Set query filter if provided
          if (queryFilter) {
            el.setAttribute('query-filter', queryFilter);
          }
          
          // Set boolean attributes
          el.setAttribute('hide-title', hideTitle.toString());
          el.setAttribute('hide-filter-button', hideFilterButton.toString());
          el.setAttribute('hide-back-to-top', hideBackToTop.toString());
          el.setAttribute('show-declined-activity', showDeclinedActivity.toString());
          el.setAttribute('show-csv-download', showCsvDownload.toString());
          
          // Add event listener with error handling
          el.addEventListener('unitOnLoad', function (e) {
            try {
              const event = e as CustomEvent;
              if (event.detail?.errors) {
                console.error('❌ Unit.co error:', event.detail.errors);
                
                // Log detailed error information
                const errors = Array.isArray(event.detail.errors) ? event.detail.errors : [event.detail.errors];
                errors.forEach((error: any, index: number) => {
                  console.error(`❌ Unit.co Error ${index + 1}:`, {
                    error,
                    status: error?.status,
                    message: error?.message,
                    detail: error?.detail,
                    timestamp: new Date().toISOString(),
                    userId: propCurrentUserId
                  });
                });
                
                // Handle 401 errors (token expired)
                if (errors[0]?.status === '401' || errors[0]?.status === 401) {
                  console.error('🚨 UnitWhiteLabelActivity: Token expired, clearing tokens and reloading page');
                  
                  // Clear user-specific tokens
                  if (propCurrentUserId) {
                    localStorage.removeItem(`unit_jwt_token_${propCurrentUserId}`);
                    localStorage.removeItem(`unit_jwt_exp_${propCurrentUserId}`);
                    localStorage.removeItem(`unitCustomerToken_${propCurrentUserId}`);
                    localStorage.removeItem(`apiType_${propCurrentUserId}`);
                    localStorage.removeItem('unitCustomerToken');
                  }
                  
                  // Reload page to get fresh token
                  window.location.reload();
                  return;
                }
                
                // Handle 500 errors - show user-friendly message
                if (errors[0]?.status === '500' || errors[0]?.status === 500) {
                  console.error('🚨 UnitWhiteLabelActivity: Server error (500), showing error message');
                  
                  if (containerRef.current) {
                    containerRef.current.innerHTML = `
                      <div class="text-center p-6">
                        <p class="text-red-600 text-lg font-semibold mb-2">Unable to load activities</p>
                        <p class="text-gray-600 text-sm mb-4">The activities service is temporarily unavailable. Please try again in a moment.</p>
                        <button 
                          onclick="window.location.reload()" 
                          class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                          Reload Page
                        </button>
                      </div>
                    `;
                  }
                  
                  // Dispatch error event for tracking
                  window.dispatchEvent(new CustomEvent('unitcoPluginError', {
                    detail: {
                      type: 'server_error',
                      status: 500,
                      errors: errors,
                      timestamp: new Date().toISOString(),
                      userId: propCurrentUserId
                    }
                  }));
                  
                  return;
                }
              }
              if (onLoadComplete) {
                onLoadComplete();
              }
            } catch (err) {
              console.error('❌ UnitWhiteLabelActivity: Error in unitOnLoad handler:', err);
            }
          });
          
          // Add general error event listener
          el.addEventListener('error', function (e: Event) {
            console.error('❌ UnitWhiteLabelActivity: Element error event:', e);
            window.dispatchEvent(new CustomEvent('unitcoPluginError', {
              detail: {
                type: 'element_error',
                event: e,
                timestamp: new Date().toISOString(),
                userId: propCurrentUserId
              }
            }));
          });
          
          // Add error handler for script load failures
          script.onerror = () => {
            console.error('❌ UnitWhiteLabelActivity: Failed to load Unit.co script');
            if (containerRef.current) {
              containerRef.current.innerHTML = '<div class="text-center p-4"><p class="text-red-600">Failed to load activities interface. Please refresh the page.</p></div>';
            }
          };
          
          // Append to container
          if (containerRef.current) {
            containerRef.current.appendChild(el);
          }
          
          // Mark as initialized
          hasInitializedRef.current = true;
        } catch (err) {
          console.error('❌ UnitWhiteLabelActivity: Error creating Unit.co element:', err);
          hasInitializedRef.current = false;
        }
      };
      
      document.body.appendChild(script);
    } catch (err) {
      console.error('❌ UnitWhiteLabelActivity: Error in useEffect:', err);
      hasInitializedRef.current = false;
    }
    
    // Cleanup function: remove script, launcher, and container content on unmount
    return () => {
      try {
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }
        const script = document.getElementById('unit-wl-activity-script');
        if (script) script.remove();
        removeUnitCoLauncher();
        hasInitializedRef.current = false;
      } catch (err) {
        console.error('❌ UnitWhiteLabelActivity: Error in cleanup:', err);
      }
    };
  }, [jwtToken, customerToken, accountId, themeUrl, languageUrl, queryFilter, hideTitle, hideFilterButton, hideBackToTop, showDeclinedActivity, showCsvDownload, onLoadComplete, propCurrentUserId]);


  // Show loading state if we don't have both requirements
  if (!propCurrentUserId || !jwtToken) {
    return (
      <div className={`rounded-2xl p-6 bg-white ${cardType === "available-balance" ? "min-h-[200px]" : "shadow-lg min-h-[600px]"} overflow-hidden relative`}>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-gray-600 text-sm">
              {!propCurrentUserId ? 'Loading user...' : 'Loading activities...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl p-6 bg-white ${cardType === "available-balance" ? "min-h-[200px]" : "shadow-lg min-h-[600px]"} overflow-hidden relative`}>
      {/* Always render Unit Elements container */}
      <div
        ref={containerRef}
        className={`w-full h-full ${cardType === "available-balance" ? "absolute opacity-0 pointer-events-none" : "overflow-auto"}`}
        style={cardType === "available-balance" ? { zIndex: -1 } : {}}
      />
      
      {cardType === "available-balance" && (
        // Show loading state for available-balance
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-gray-600 text-sm">Loading...</p>
          </div>
        </div>
      )}
    </div>
  );
  
};

export default UnitWhiteLabelActivity;

